import type { QueryClient } from "@tanstack/react-query";
import createFetchClient, { type Middleware } from "openapi-fetch";
import createClient from "openapi-react-query";

import { API_ORIGIN } from "@/constants";
import { getTenantId } from "@/global/TenantGuard";
import { setLocalStorageTenantId } from "@/global/TenantGuard/localStorage";
import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  jwtClaims,
  setAccessToken,
} from "@/utils/auth";

import type { paths } from "./schema";

/** A non-2xx response. `error` is the API's `{error: {...}}` envelope. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly error?: {
      code?: string;
      message?: string;
      details?: unknown;
      [key: string]: unknown;
    },
  ) {
    super(error?.message ?? `HTTP ${status}`);
  }
}

export const isNotFound = (error: unknown) =>
  error instanceof ApiError && error.status === 404;

/** The response's data, or null when it is a 404. */
export async function orNull<T>(
  request: Promise<{ data?: T }>,
): Promise<T | null> {
  try {
    return (await request).data!;
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

// Every response that isn't ok throws, so queries and mutations see one error type.
const tenantAndErrors: Middleware = {
  onRequest({ request }) {
    const tenant = getTenantId();
    if (tenant) request.headers.set("Victoria-Tenant", tenant);
    return request;
  },
  async onResponse({ response }) {
    if (response.ok) return response;
    if (response.status === 403) {
      setLocalStorageTenantId(null);
      window.location.href = "/";
    }
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, body?.error);
  },
};

/** For calls made without a login: tokens and the tenant lookup. */
export const publicClient = createFetchClient<paths>({ baseUrl: API_ORIGIN });
publicClient.use(tenantAndErrors);

let refreshing: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  try {
    const { data } = await publicClient.POST("/api/token/refresh/", {
      body: { refresh: getRefreshToken()! },
    });
    setAccessToken(data!.access);
    return data!.access;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      clearAuthTokens();
      window.location.href = "/";
    }
    throw error;
  }
}

/** The access token, refreshed once for all callers when it is about to expire. */
async function accessToken(): Promise<string | undefined> {
  const token = getAccessToken();
  if (!token || jwtClaims(token).exp - Date.now() / 1000 > 10) return token;
  refreshing ??= refreshAccessToken().finally(() => (refreshing = null));
  return refreshing;
}

export const fetchClient = createFetchClient<paths>({ baseUrl: API_ORIGIN });
fetchClient.use(tenantAndErrors, {
  async onRequest({ request }) {
    const token = await accessToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
});

export const $api = createClient(fetchClient);

/** A table's sort id as the one-value ordering list django-filter's
 *  `OrderingFilter` documents as an enum; the column ids aren't typed against
 *  it, so the server's 400 on an unknown id is the check. */
export const orderingParam = (ordering?: string) =>
  ordering ? ([ordering] as never[]) : undefined;

/** Invalidates every query whose path starts with one of `prefixes`
 *  (keys are `[method, path, init]`). */
export function invalidate(queryClient: QueryClient, ...prefixes: string[]) {
  return queryClient.invalidateQueries({
    predicate: ({ queryKey: [, path] }) =>
      prefixes.some((p) => String(path).startsWith(p)),
  });
}
